import { DataItem } from 'scrivito'

import { LogTimeFormData } from '../types'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'

const BATCH_SIZE = 5
const BATCH_DELAY_MS = 250

const buildPayload = (data: LogTimeFormData) => ({
  recordedAt: data.date,
  client: data.customer,
  order: data.order,
  position: data.workPackagePosition,
  ...(data.workPackageBudget ? { budget: data.workPackageBudget } : {}),
  totalDuration: Number(data.workDuration) || 0,
  billedDuration: Number(data.billedDuration) || 0,
  description: data.description,
  internalNote: data.internalNote,
  activityType: data.typeOfActivity,
})

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

const chunk = <T>(list: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, i) =>
    list.slice(i * size, i * size + size),
  )

const dedupeByValue = <T extends { value: string }>(list: T[]): T[] => [
  ...new Map(list.map((o) => [o.value, o])).values(),
]

export class TimeRecordSubmissionService {
  static async submitTimeRecord(data: LogTimeFormData): Promise<DataItem[]> {
    const payload = buildPayload(data)
    const users = dedupeByValue(data.users)
    const batches = chunk(users, BATCH_SIZE)
    const results: PromiseSettledResult<DataItem>[] = []

    for (const [idx, batch] of batches.entries()) {
      if (idx > 0) await sleep(BATCH_DELAY_MS)
      const settled = await Promise.allSettled(
        batch.map((u) => Timerecord.create({ ...payload, user: u.value })),
      )
      results.push(...settled)
    }

    const created = results.flatMap((r) =>
      r.status === 'fulfilled' ? [r.value] : [],
    )
    const failedCount = results.length - created.length

    if (failedCount === 0) return created

    await Promise.allSettled(created.map((item) => item.delete()))
    throw new Error(
      `Failed to create ${failedCount}/${results.length} entries — rolled back`,
    )
  }

  static async updateTimeRecord(
    recordId: string,
    data: LogTimeFormData,
  ): Promise<DataItem> {
    const record = Timerecord.get(recordId)
    if (!record) {
      throw new Error(`Time record with ID ${recordId} not found`)
    }

    record.update(buildPayload(data))

    return record
  }
}
